import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { termsPrivacyService } from '../services/termsPrivacyService';
import { User } from '../types/alarm';

interface TermsAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (agreedTerms: boolean, agreedPrivacy: boolean) => void;
  onWithdraw?: (userEmail: string) => void; // 회원탈퇴 핸들러 (이메일 전달)
  userEmail?: string; // 사용자 이메일
  user?: User | null; // 이용권 안내/경고용 (회원탈퇴 시)
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  /* 모달 밖 클릭으로 닫히지 않도록 함 */
  pointer-events: auto;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalTitle = styled.h2`
  color: var(--color-text-primary);
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
`;

const AllAgreeSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--color-panel);
  border-radius: 8px;
  border: 2px solid var(--color-border);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--color-accent-strong);
  }
`;

const AllAgreeCheckbox = styled.input`
  width: 24px;
  height: 24px;
  cursor: pointer;
  accent-color: var(--color-accent-strong);
`;

const AllAgreeLabel = styled.label`
  color: var(--color-text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  user-select: none;
`;

const AgreementSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AgreementHeader = styled.div`
  padding: 12px;
  background: var(--color-panel);
  border-radius: 8px;
`;

const AgreementTitle = styled.div`
  color: var(--color-text-primary);
  font-size: 1rem;
  font-weight: 600;
  user-select: none;
`;

const AgreementContent = styled.div`
  padding: 16px;
  background: var(--color-bg);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  max-height: 300px;
  overflow-y: auto;
  line-height: 1.6;
  color: var(--color-text-secondary);
  
  p {
    margin: 0 0 12px 0;
    color: var(--color-text-secondary);
  }
  
  h2 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 16px 0 8px 0;
  }
  
  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 12px 0 6px 0;
  }
  
  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
    color: var(--color-text-secondary);
  }
  
  li {
    margin: 4px 0;
    color: var(--color-text-secondary);
  }
  
  /* 다크모드에서 모든 텍스트를 흰색으로 */
  [data-theme='dark'] &,
  :root:not([data-theme='light']) & {
    color: #ffffff !important;
    
    p, span, div, li, ul, ol {
      color: #ffffff !important;
    }
    
    h2, h3, h4, h5, h6 {
      color: #ffffff !important;
    }
    
    * {
      color: #ffffff !important;
    }
  }
`;

const AgreementCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 0;
  margin-top: 8px;
`;

const AgreementCheckbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--color-accent-strong);
`;

const AgreementCheckboxLabel = styled.label`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  user-select: none;
`;

const LoadingText = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  padding: 40px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const WithdrawButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 12px 0;
  margin-top: 8px;
  text-decoration: underline;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--color-text-primary);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ConfirmModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10002;
`;

const ConfirmModalContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 500px;
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: 10003;
  max-height: 90vh;
  overflow-y: auto;
`;

const ConfirmTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
`;

const ConfirmMessage = styled.p`
  margin: 0 0 12px 0;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text-primary);
  text-align: left;
  
  strong {
    font-weight: 600;
  }
`;

const ConfirmWarning = styled.div`
  margin: 16px 0;
  padding: 12px;
  background: var(--color-panel);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  text-align: left;
`;

const ConfirmWarningLicense = styled(ConfirmWarning)`
  font-weight: 700;
  color: #ffffff;
  [data-theme='light'] & {
    color: var(--color-accent-strong);
  }
`;

const ConfirmInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  background: var(--color-surface);
  color: var(--color-text-primary);
  margin: 12px 0;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: var(--color-accent-strong);
  font-size: 0.9rem;
  margin: 8px 0;
`;

const ConfirmButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const DeleteConfirmButton = styled.button<{ destructive?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.destructive ? `
    background: var(--color-accent-strong);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--color-accent);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
  ` : `
    background: var(--color-surface);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border);
    
    &:hover:not(:disabled) {
      background: var(--color-panel);
      border-color: var(--color-text-muted);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const REFUND_ELIGIBLE_DAYS = 7;

const getLicenseStatus = (u: User | null | undefined): { refundEligible: boolean; hasRemainingPeriod: boolean } => {
  if (!u?.license) return { refundEligible: false, hasRemainingPeriod: false };
  const purchasedAt = u.license.purchasedAt instanceof Date
    ? u.license.purchasedAt
    : new Date((u.license.purchasedAt as any)?.toDate?.() ?? u.license.purchasedAt);
  const expiresAt = u.license.expiresAt instanceof Date
    ? u.license.expiresAt
    : new Date((u.license.expiresAt as any)?.toDate?.() ?? u.license.expiresAt);
  if (isNaN(purchasedAt.getTime()) || isNaN(expiresAt.getTime())) {
    return { refundEligible: false, hasRemainingPeriod: false };
  }
  const now = Date.now();
  const sevenDaysMs = REFUND_ELIGIBLE_DAYS * 24 * 60 * 60 * 1000;
  const refundEligible = now - purchasedAt.getTime() < sevenDaysMs;
  const hasRemainingPeriod = expiresAt.getTime() > now;
  return { refundEligible, hasRemainingPeriod };
};

const TermsAgreementModal: React.FC<TermsAgreementModalProps> = ({ isOpen, onClose, onConfirm, onWithdraw, userEmail, user }) => {
  const [termsContent, setTermsContent] = useState<string>('');
  const [privacyContent, setPrivacyContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [agreedTerms, setAgreedTerms] = useState<boolean>(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState<boolean>(false);
  const [allAgreed, setAllAgreed] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [confirmText, setConfirmText] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadTermsAndPrivacy();
    } else {
      // 모달이 닫힐 때 상태 초기화
      setAgreedTerms(false);
      setAgreedPrivacy(false);
      setAllAgreed(false);
      setShowDeleteConfirm(false);
      setConfirmText('');
      setDeleteError('');
      setIsDeleting(false);
    }
  }, [isOpen]);

  const loadTermsAndPrivacy = async () => {
    setLoading(true);
    try {
      const [terms, privacy] = await Promise.all([
        termsPrivacyService.getTermsOfUse(),
        termsPrivacyService.getPrivacyPolicy()
      ]);
      
      setTermsContent(terms?.content || '<p>서비스 이용약관 내용이 없습니다.</p>');
      setPrivacyContent(privacy?.content || '<p>개인정보처리방침 내용이 없습니다.</p>');
    } catch (error) {
      console.error('약관 로드 실패:', error);
      setTermsContent('<p>서비스 이용약관을 불러오는 데 실패했습니다.</p>');
      setPrivacyContent('<p>개인정보처리방침을 불러오는 데 실패했습니다.</p>');
    } finally {
      setLoading(false);
    }
  };

  const handleAllAgreeChange = (checked: boolean) => {
    setAllAgreed(checked);
    setAgreedTerms(checked);
    setAgreedPrivacy(checked);
  };

  const handleTermsChange = (checked: boolean) => {
    setAgreedTerms(checked);
    setAllAgreed(checked && agreedPrivacy);
  };

  const handlePrivacyChange = (checked: boolean) => {
    setAgreedPrivacy(checked);
    setAllAgreed(checked && agreedTerms);
  };

  const handleConfirm = () => {
    if (agreedTerms && agreedPrivacy) {
      onConfirm(agreedTerms, agreedPrivacy);
      // onClose()는 호출하지 않음 - handleTermsAgreementConfirm에서 모달을 닫음
    }
  };

  const handleWithdrawClick = () => {
    const { refundEligible } = getLicenseStatus(user);
    if (refundEligible) {
      alert('이용권 구매 후 7일 이내에는 환불이 가능합니다. 먼저 환불을 진행한 후 회원탈퇴를 진행해 주세요.');
      return;
    }
    setShowDeleteConfirm(true);
    setConfirmText('');
    setDeleteError('');
  };

  const handleDeleteAccount = async () => {
    if (!onWithdraw || !userEmail) return;
    if (isDeleting) return;
    
    const isConfirmed = confirmText.trim() === userEmail;
    if (!isConfirmed) {
      setDeleteError('계정 이메일을 입력해주세요.');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      await onWithdraw(userEmail);
      // onWithdraw에서 모든 처리를 완료하면 모달이 닫힘
    } catch (error: any) {
      console.error('회원탈퇴 실패:', error);
      setDeleteError(error?.message || '회원탈퇴에 실패했습니다. 다시 시도해주세요.');
      setIsDeleting(false);
    }
  };

  const canDelete = confirmText.trim() === userEmail;

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalTitle>약관 동의</ModalTitle>

        <AgreementSection>
          <AgreementHeader>
            <AgreementTitle>이용약관 동의(필수)</AgreementTitle>
          </AgreementHeader>
          <AgreementContent>
            {loading ? (
              <LoadingText>로딩 중...</LoadingText>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: termsContent }} />
            )}
          </AgreementContent>
          <AgreementCheckboxContainer>
            <AgreementCheckbox
              type="checkbox"
              id="terms-checkbox"
              checked={agreedTerms}
              onChange={(e) => handleTermsChange(e.target.checked)}
            />
            <AgreementCheckboxLabel htmlFor="terms-checkbox">
              동의합니다.
            </AgreementCheckboxLabel>
          </AgreementCheckboxContainer>
        </AgreementSection>

        <AgreementSection>
          <AgreementHeader>
            <AgreementTitle>개인정보 수집 및 이용 동의(필수)</AgreementTitle>
          </AgreementHeader>
          <AgreementContent>
            {loading ? (
              <LoadingText>로딩 중...</LoadingText>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: privacyContent }} />
            )}
          </AgreementContent>
          <AgreementCheckboxContainer>
            <AgreementCheckbox
              type="checkbox"
              id="privacy-checkbox"
              checked={agreedPrivacy}
              onChange={(e) => handlePrivacyChange(e.target.checked)}
            />
            <AgreementCheckboxLabel htmlFor="privacy-checkbox">
              동의합니다.
            </AgreementCheckboxLabel>
          </AgreementCheckboxContainer>
        </AgreementSection>

        <AllAgreeSection onClick={() => handleAllAgreeChange(!allAgreed)}>
          <AllAgreeCheckbox
            type="checkbox"
            checked={allAgreed}
            onChange={(e) => handleAllAgreeChange(e.target.checked)}
          />
          <AllAgreeLabel>모두 동의</AllAgreeLabel>
        </AllAgreeSection>

        <ButtonGroup>
          <CancelButton onClick={onClose}>취소</CancelButton>
          <ConfirmButton 
            disabled={!agreedTerms || !agreedPrivacy}
            onClick={handleConfirm}
          >
            저장
          </ConfirmButton>
        </ButtonGroup>
        
        {onWithdraw && (
          <WithdrawButton onClick={handleWithdrawClick}>
            회원탈퇴
          </WithdrawButton>
        )}
      </ModalContent>
      
      {showDeleteConfirm && (
        <>
          <ConfirmModalOverlay onClick={() => !isDeleting && setShowDeleteConfirm(false)} />
          <ConfirmModalContent>
            <ConfirmTitle>회원탈퇴</ConfirmTitle>
            <ConfirmMessage>
              회원탈퇴 시 모든 데이터가 영구 삭제되며 복구할 수 없습니다. 계속하시겠습니까?
            </ConfirmMessage>
            {getLicenseStatus(user).hasRemainingPeriod && (
              <ConfirmWarningLicense>
                사용 기간이 남은 이용권이 있습니다. 지금 회원 탈퇴를 하시면 이용권은 소멸되며 복구할 수 없습니다.
              </ConfirmWarningLicense>
            )}
            <ConfirmWarning>
              데이터베이스의 사용자 문서(클래스, 멤버, 알람, 과제, 포인트, 기록 등)의 모든 데이터가 즉시 삭제됩니다.<br />
              데이터베이스의 사용자 정보도 삭제되어 다시 로그인 할 수 없습니다. (로그인을 위해 재가입 필요)
            </ConfirmWarning>
            <ConfirmMessage>
              인증을 위해 계정 이메일을 입력하세요.
            </ConfirmMessage>
            <ConfirmInput
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="example@edukodi.com"
              disabled={isDeleting}
            />
            {deleteError && <ErrorText>{deleteError}</ErrorText>}
            <ConfirmButtonRow>
              <DeleteConfirmButton onClick={() => !isDeleting && setShowDeleteConfirm(false)} disabled={isDeleting}>
                취소
              </DeleteConfirmButton>
              <DeleteConfirmButton
                destructive
                onClick={handleDeleteAccount}
                disabled={!canDelete || isDeleting}
              >
                {isDeleting ? '회원탈퇴 중...' : '회원탈퇴'}
              </DeleteConfirmButton>
            </ConfirmButtonRow>
          </ConfirmModalContent>
        </>
      )}
    </ModalOverlay>
  );
};

export default TermsAgreementModal;

